import React, { useMemo, useState } from "react";
import { ReferenceFile } from "../../../types/ReferenceFile";
import LegalbirdLoader from "../../ContentLoader/LegalbirdLoader";
import MediaObjectFolderView from "../../MediaObjectSection/MediaObjectFolderView";
import ContentBox from "../../ContentBox/ContentBox";
import ApiClient from "../../../services/ApiClient";
import ButtonLoading from "../../Button/ButtonLoading";
import { Box, Button, Stack, Typography } from "@mui/material";
import caseReferenceFileStyles from "./caseReferenceFileStyles";
import _ from "lodash";
import LegalbirdIoConfirm from "../../Modal/LegalbirdIoConfirm";
import { useSnackbar } from "notistack";
import { getCaseLink } from "../../../services/Product/ProductService/productService";
import { MediaObject } from "../../../types/MediaObject";
import { useQuery } from "@tanstack/react-query";
import { fetchCollection, queryKeys } from "../../../services/ReactQuery/reactQueryService";
import { useNavigate } from "react-router-dom";
import {
  getMediaObjectIdsFromSelectedMediaObjects,
  saveSelectedMediaObjects,
} from "../../../services/referenceFileService";
import { ProductUnion } from "../../../types/ProductUnion";
import dayjs from "dayjs";

type CaseReferenceFileProps = {
  product: ProductUnion;
  referenceFile: ReferenceFile;
  refreshReferenceFile: () => void;
};

export default function CaseReferenceFile({ product, referenceFile, refreshReferenceFile }: CaseReferenceFileProps) {
  const [selectedMediaObjects, setSelectedMediaObjects] = useState(
    getMediaObjectIdsFromSelectedMediaObjects(referenceFile.selectedMediaObjects)
  );
  const [isLoading, setIsLoading] = useState(false);
  const [sendConfirmationModalOpen, setSendConfirmationModalOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  let mediaObjectsFilter = {
    product: product.productClassName,
    productId: product.id,
  };
  const { data: mediaObjects } = useQuery({
    queryKey: queryKeys.collection("media_objects", mediaObjectsFilter),
    queryFn: () => fetchCollection("media_objects", mediaObjectsFilter),
    enabled: !!product
  });

  const filteredMediaObjects = useMemo(() => {
    if (!mediaObjects) {
      return null;
    }
    return mediaObjects["hydra:member"].filter(
      (mediaObject: MediaObject) =>
        mediaObject.showInExternalView || mediaObject.showInGallery || mediaObject.showInReferenceFileSelection
    );
  }, [mediaObjects]);

  if (!mediaObjects) {
    return <LegalbirdLoader centered />;
  }

  const courtAppointment = _.find(
    product.courtAppointments,
    (appointment) => appointment.id + "" === referenceFile.courtAppointmentId
  );

  if (dayjs(courtAppointment?.courtDate?.appointmentDateTime).add(1, "day") < dayjs()) {
    navigate(getCaseLink(product));
  }

  const handleFileSave = async () => {
    setIsLoading(true);
    try {
      await saveSelectedMediaObjects(referenceFile, selectedMediaObjects);
      enqueueSnackbar("Handakte wurde erfolgreich gespeichert", {
        variant: "custom",
        isNonInteractive: true,
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
      });
    } catch (error) {
      enqueueSnackbar("Es ist ein Fehler aufgetreten", {
        variant: "custom",
        isNonInteractive: true,
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
      });
    }

    await refreshReferenceFile();
    setIsLoading(false);
  };

  const handleSendReferenceFile = async () => {
    setIsLoading(true);
    await saveSelectedMediaObjects(referenceFile, selectedMediaObjects);

    try {
      await ApiClient.post(`/send_reference_file/${referenceFile.id}`);
      enqueueSnackbar("Handakte wurde erfolgreich versendet", {
        variant: "custom",
        buttonType: "link",
        buttonLink: getCaseLink(product),
        buttonTarget: "_self",
        buttonText: "Zum Fall",
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
      });
    } catch (e) {
      enqueueSnackbar("Es ist ein Fehler aufgetreten", {
        variant: "custom",
        buttonType: "link",
        buttonLink: getCaseLink(product),
        buttonTarget: "_self",
        buttonText: "Zum Fall",
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
      });
    }

    await refreshReferenceFile();
    setIsLoading(false);
  };

  return (
    <ContentBox headline={"Dokumente"}>
      <Stack spacing={2} sx={{
        alignItems: "center"
      }}>
        <Box sx={{ width: "100%" }}>
          <MediaObjectFolderView
            mediaObjects={filteredMediaObjects}
            selectableView
            selectedMediaObjects={selectedMediaObjects}
            setSelectedMediaObjects={setSelectedMediaObjects}
          />
        </Box>
        <ButtonLoading
          isLoading={isLoading}
          onClick={handleFileSave}
          variant={"outlined"}
          sx={caseReferenceFileStyles.button}
        >
          Auswahl speichern
        </ButtonLoading>
        <Button
          onClick={() => setSendConfirmationModalOpen(true)}
          variant={"contained"}
          sx={caseReferenceFileStyles.button}
          disabled={
            (!_.get(courtAppointment, "advoAssist.partnerLawyer") &&
              !_.get(courtAppointment, "advoAssistIndependentLawyer.email")) ||
            selectedMediaObjects.length < 1
          }
        >
          Handakte versenden
        </Button>
        {referenceFile.lastSent && (
          <>
            <Typography sx={{ textAlign: "center" }}>
              Wenn der TV über neue Dateien in der Handakte informiert werden soll, einfach die neuen Dateien auswählen,
              die Auswahl speichern und erneut auf “Handakte versenden” klicken.
            </Typography>
            <Typography>
              Handakte wurde zuletzt versendet am {dayjs(referenceFile.lastSent).format("DD.MM.YYYY, HH:mm")} Uhr
            </Typography>
          </>
        )}
        <LegalbirdIoConfirm
          handleClose={() => setSendConfirmationModalOpen(false)}
          open={sendConfirmationModalOpen}
          headline={"Handakte versenden"}
          handleConfirm={handleSendReferenceFile}
          confirmText={"Handakte versenden"}
          content={
            'Mit einem Klick auf "Handakte versenden" erhält der Terminsvertreter eine E-Mail mit Zugang zur Handakte, welcher dann bis zum Gerichtstermin gültig ist.'
          }
        />
      </Stack>
    </ContentBox>
  );
}
